import React, { createContext, useContext, ReactNode } from 'react';
import { trackEvent, trackPurchase, trackAddToCart, trackViewItem, trackBeginCheckout } from './GoogleAnalytics';
import { fbTrackEvent, fbTrackPurchase, fbTrackAddToCart, fbTrackViewContent, fbTrackInitiateCheckout } from './FacebookPixel';
import { productionLogger } from '@/utils/production-logger';

interface ConversionTrackingContextType {
  trackPurchaseEvent: (orderId: string, value: number, items: any[]) => void;
  trackAddToCartEvent: (productId: string, name: string, value: number) => void;
  trackViewProductEvent: (productId: string, name: string, value: number) => void;
  trackBeginCheckoutEvent: (value: number, items: any[]) => void;
  trackSearchEvent: (searchTerm: string) => void;
  trackCustomEvent: (eventName: string, properties: Record<string, any>) => void;
}

const ConversionTrackingContext = createContext<ConversionTrackingContextType | undefined>(undefined);

interface ConversionTrackingProviderProps {
  children: ReactNode;
}

export const ConversionTrackingProvider: React.FC<ConversionTrackingProviderProps> = ({ children }) => {
  const trackPurchaseEvent = (orderId: string, value: number, items: any[]) => {
    
    // Google Analytics
    trackPurchase(orderId, value, 'USD', items);
    
    // Facebook Pixel
    const contentIds = items.map(item => item.item_id || item.id);
    fbTrackPurchase(value, 'USD', contentIds);
  };

  const trackAddToCartEvent = (productId: string, name: string, value: number) => {
    
    const item = {
      item_id: productId,
      item_name: name,
      item_category: 'Boho Fashion',
      price: value,
      quantity: 1
    };

    // Google Analytics
    trackAddToCart('USD', value, [item]);
    
    // Facebook Pixel
    fbTrackAddToCart(value, 'USD', productId);
  };

  const trackViewProductEvent = (productId: string, name: string, value: number) => {
    
    const item = {
      item_id: productId,
      item_name: name,
      item_category: 'Boho Fashion',
      price: value,
      quantity: 1
    };

    // Google Analytics
    trackViewItem('USD', value, [item]);
    
    // Facebook Pixel
    fbTrackViewContent(value, 'USD', productId);
  };

  const trackBeginCheckoutEvent = (value: number, items: any[]) => {
    
    // Google Analytics
    trackBeginCheckout('USD', value, items);
    
    // Facebook Pixel
    const contentIds = items.map(item => item.item_id || item.id);
    fbTrackInitiateCheckout(value, 'USD', contentIds);
  };

  const trackSearchEvent = (searchTerm: string) => {
    
    // Google Analytics
    trackEvent('search', { search_term: searchTerm });
    
    // Facebook Pixel
    fbTrackEvent('Search', { search_string: searchTerm });
  };

  const trackCustomEvent = (eventName: string, properties: Record<string, any>) => {
    // Google Analytics
    trackEvent(eventName, properties);
    
    // Facebook Pixel
    fbTrackEvent('CustomEvent', { event_name: eventName, ...properties });
  };

  const value = {
    trackPurchaseEvent,
    trackAddToCartEvent,
    trackViewProductEvent,
    trackBeginCheckoutEvent,
    trackSearchEvent,
    trackCustomEvent
  };

  return (
    <ConversionTrackingContext.Provider value={value}>
      {children}
    </ConversionTrackingContext.Provider>
  );
};

export const useConversionTracking = (): ConversionTrackingContextType => {
  const context = useContext(ConversionTrackingContext);
  if (!context) {
    throw new Error('useConversionTracking must be used within a ConversionTrackingProvider');
  }
  return context;
};